import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import { Button, Tooltip } from 'src/components/ui';
import { useDarkMode } from '../../../context/DarkModeContext';
import { Trans } from '@lingui/react/macro';
import UWEFormContext from '../../../context/UWEFormContext';
import Loader from '../../Loader';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { OcifXlsxApi, triggerBlobDownload, FinancialXlsxUploadResult } from '../../../api/OcifXlsxApi';
import UWEEntityApi from '../../../api/UWEEntityApi';
import FileApi from '../../../api/FileApi';
import { FieldPathId, Registry } from '@rjsf/utils';
import { useSideChannelSubscription } from '../../../util/useSideChannel';
import { AuthContext } from '../../../context/AuthContext';

import { BulkUploadFormData, FileStage, UploadAttempts, UploadStatus, ValidationState } from './types';
import { buildDropZoneCss, INITIAL_FILE_STAGE, INVENTORY_SCHEDULES_TAB, STATUS_COLOR, STATUS_ICON } from './util';
import { FinancialNoticePanel, GlobalErrorPanel, IgnoredSchedulesNotice } from './FinancialResultPanel';
import { ReplaceWarningModal } from './ReplaceWarningModal';
import { FinancialDropZone } from './FinancialDropZone';
import { FilingStatusCard } from './FilingStatusCard';
import { ApiFilingNote } from './ApiFilingNote';
import { ScheduleLinkList } from './ScheduleLinkList';
import { useReportSchedules } from './useReportSchedules';
import { useScheduleStatuses } from './useScheduleStatuses';
import { useOpenScheduleTab } from './useOpenScheduleTab';
import { AccordionItem } from '../../ui/Accordion';
import FilterChip from '../../FilterChip';
import Icon from 'src/components/Icon';
import { ConflictsPanel } from '../../../blc/ConflictsPanel';

const FINANCIAL_UPLOAD_STATUSES: UploadStatus[] = ['Not Uploaded', 'Uploaded', 'Partial', 'Error', 'Done'];
const ALL_UPLOAD_STATUSES: UploadStatus[] = [...FINANCIAL_UPLOAD_STATUSES, 'Nothing to Report'];

function OcifBulkUploadPanel({
  formData = {},
  registry,
  fieldPathId,
  readonly = false,
}: {
  formData?: BulkUploadFormData;
  registry: Registry;
  fieldPathId: FieldPathId;
  readonly?: boolean;
}) {
  const auth = useContext(AuthContext);
  const { isDarkMode } = useDarkMode();

  const UweFormContext = useContext(UWEFormContext);
  const currentEntity = UweFormContext?.currentEntity;
  const formFieldsById = UweFormContext?.formFieldsById ?? {};
  const navigateToField = UweFormContext?.navigateToField ?? (() => {});
  const entityId = currentEntity?.id as number | undefined;

  const openScheduleTab = useOpenScheduleTab(fieldPathId);

  const theme = useTheme() as { primaryMain: string; gray400: string };
  const isMobile = useIsMobile();

  const rootFormData = useSideChannelSubscription(registry?.formContext?.sideChannel, [0]);

  const conflicts = (rootFormData as any)?.conflicts ?? {};

  const { scheduleTitles } = useReportSchedules(currentEntity);

  const financialXLSX = formData?.financialXLSX ?? null;
  const scheduleStatuses = useScheduleStatuses(registry, financialXLSX);

  // ── Local staging state ──────────────────────────────────────────────
  const [financialStage, setFinancialStage] = useState<FileStage>(INITIAL_FILE_STAGE);
  const [financialResult, setFinancialResult] = useState<FinancialXlsxUploadResult | null>(null);
  const [financialTemplateLoading, setFinancialTemplateLoading] = useState(false);

  const showGlobalError = financialResult?.status === 'error' && (financialResult.globalErrors?.length ?? 0) > 0;
  const showNotice = !!(financialResult && financialResult.status !== 'error' && financialResult.status !== 'requiresConfirmation');
  const showIgnored = !!(
    financialResult &&
    Object.values(financialResult.scheduleResults ?? {}).some(sr => sr.status === 'ignored' && sr.ignoredReason === 'not-in-reporting-window')
  );
  const statusPanelCount = [showGlobalError, showNotice, showIgnored].filter(Boolean).length;
  const uploadAttemptsLoaded = useRef(false);

  // ── Load last upload attempt on mount ────────────────────────────────
  useEffect(() => {
    if (uploadAttemptsLoaded.current || !entityId) return;
    const uploadAttempts: UploadAttempts[] = currentEntity?.data?.uploadAttempts;
    if (!uploadAttempts?.length) {
      // Fallback: load from financialXLSX form data if available
      if (financialXLSX?.fileName) {
        uploadAttemptsLoaded.current = true;
        setFinancialStage({
          file: new File([], financialXLSX.fileName),
          validationState: 'valid',
          preloadedFileName: financialXLSX.fileName,
          uploadedAt: financialXLSX.uploadedAt,
        });
      }
      return;
    }
    uploadAttemptsLoaded.current = true;

    const last = uploadAttempts[uploadAttempts.length - 1];
    const fileId = last?.uploadedFileId;
    const uploadDate: string | undefined = last?.uploadDate;
    if (!fileId) return;

    setFinancialResult(last?.uploadResult || null);

    (async () => {
      try {
        const token = await UWEEntityApi.createWfAccessTokenHash({
          uweEntityId: entityId,
          canView: true,
          canUpdate: true,
          canCreate: true,
        });
        const fileMeta = await FileApi.getFile({ fileId, wfAccessToken: token });
        const fakeFile = new File([], fileMeta.name);
        setFinancialStage({
          file: fakeFile,
          validationState: 'valid',
          preloadedFileName: fileMeta.name,
          uploadedAt: uploadDate,
        });
      } catch (e) {
        console.error('Failed to load last upload attempt', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replace-confirmation modal state
  const [pendingReplace, setPendingReplace] = useState<{
    file: File;
    result: FinancialXlsxUploadResult;
  } | null>(null);

  const [activeStatuses, setActiveStatuses] = useState<Set<UploadStatus>>(() => new Set(ALL_UPLOAD_STATUSES));
  const [statusInfoOpen, setStatusInfoOpen] = useState(false);
  const toggleStatus = (status: UploadStatus) =>
    setActiveStatuses(prev => {
      const next = new Set(prev);
      next.has(status) ? next.delete(status) : next.add(status);
      return next;
    });

  // ── Financial handlers ────────────────────────────────────────────────

  const handleFinancialFileSelected = (file: File) => {
    setFinancialStage({ file, validationState: 'idle' });
    setFinancialResult(null);
  };

  const handleFinancialValidate = useCallback(
    async (file?: File, confirmed = false) => {
      const targetFile = file ?? financialStage.file;
      if (!targetFile || !entityId) return;

      setFinancialStage(prev => ({ ...prev, file: targetFile, validationState: 'pending' }));

      try {
        const result = await OcifXlsxApi.uploadFinancialXlsx(entityId, targetFile, confirmed);

        if (result.status === 'requiresConfirmation') {
          setFinancialStage(prev => ({ ...prev, validationState: 'idle' }));
          setPendingReplace({ file: targetFile, result });
          return;
        }

        const newState: ValidationState = result.status === 'error' ? 'error' : result.status === 'partial' ? 'notice' : 'valid';
        const errorMsg = result.status === 'error' ? result.globalErrors?.[0]?.message ?? 'Upload failed.' : undefined;

        if ((result.status === 'success' || result.status === 'partial') && entityId) {
          try {
            const freshToken = await UWEEntityApi.createWfAccessTokenHash({
              uweEntityId: entityId,
              canView: true,
              canUpdate: true,
              canCreate: true,
            });
            const freshEntity = await UWEEntityApi.getUWEEntity({ entityId, wfAccessToken: freshToken });
            UweFormContext?.setCurrentEntity?.(freshEntity);
            const attempts: any[] = freshEntity?.data?.uploadAttempts;
            const lastDate: string | undefined = attempts?.length ? attempts[attempts.length - 1]?.uploadDate : undefined;
            setFinancialStage(prev => ({
              ...prev,
              validationState: newState,
              errorMessage: errorMsg,
              ...(lastDate ? { uploadedAt: lastDate } : {}),
            }));
          } catch (e) {
            console.error('Failed to refresh entity after upload', e);
            setFinancialStage(prev => ({ ...prev, validationState: newState, errorMessage: errorMsg }));
          }
        } else {
          setFinancialStage(prev => ({ ...prev, validationState: newState, errorMessage: errorMsg }));
        }

        setFinancialResult(result);
      } catch (err) {
        setFinancialStage(prev => ({
          ...prev,
          validationState: 'error',
          errorMessage: (err as Error)?.message ?? 'Unexpected error.',
        }));
      }
    },
    [financialStage.file, entityId, UweFormContext]
  );

  const handleFinancialReplace = () => {
    setFinancialStage(INITIAL_FILE_STAGE);
    setFinancialResult(null);
  };

  const handleFinancialDownload = async () => {
    if (!entityId || financialTemplateLoading) return;
    setFinancialTemplateLoading(true);
    try {
      const blob = await OcifXlsxApi.downloadFinancialTemplate(entityId);
      const orgName = auth?.user?.user?.organizationalUnit?.name ?? 'org';
      const period = currentEntity?.metadata?.reportingWindowPeriod ?? 'report';
      const filename = `${orgName}-${period}-report.xlsx`.replace(/[/\\:*?"<>|]/g, '-');
      triggerBlobDownload(blob, filename);
    } catch (err) {
      alert((err as Error)?.message ?? 'Could not download template.');
    } finally {
      setFinancialTemplateLoading(false);
    }
  };

  const handleReplaceConfirm = () => {
    if (!pendingReplace) return;
    const { file } = pendingReplace;
    setPendingReplace(null);
    handleFinancialValidate(file, true);
  };

  const handleReplaceCancel = () => {
    setPendingReplace(null);
    setFinancialStage(INITIAL_FILE_STAGE);
    setFinancialResult(null);
  };

  const hasInventorySchedules = scheduleStatuses.inventory.length > 0;
  const statusChips = hasInventorySchedules ? ALL_UPLOAD_STATUSES : FINANCIAL_UPLOAD_STATUSES;
  const visibleFinancialRows = scheduleStatuses.financial.filter(row => activeStatuses.has(row.status));
  const visibleInventoryRows = scheduleStatuses.inventory.filter(row => activeStatuses.has(row.status));
  const noMatchesMessage = <Trans>No schedules match the selected filters</Trans>;

  return (
    <>
      <style>
        {buildDropZoneCss(theme.primaryMain) +
          `
        .status-info-tooltip .tooltip-inner { max-width: 420px; text-align: left; }
      `}
      </style>

      {pendingReplace && (
        <ReplaceWarningModal
          fileName={pendingReplace.file.name}
          info={pendingReplace.result.replacementInfo!}
          rowCount={pendingReplace.result.totalRows}
          onConfirm={handleReplaceConfirm}
          onCancel={handleReplaceCancel}
        />
      )}

      <div className="bulk-upload-panel">
        {/* ── STATUS CARD ── (an inventory-only report shows it in the inventory panel instead) */}
        {scheduleTitles.length > 0 && (
          <FilingStatusCard
            completedCount={scheduleStatuses.completedCount}
            totalCount={scheduleStatuses.totalCount}
            dueDate={currentEntity?.metadata?.dueDate}
          />
        )}

        {/* ── FILING OPTIONS ACCORDION ── */}
        <AccordionItem
          defaultOpen={false}
          title={
            <span className="form-subtitle">
              <Trans>Filing options</Trans>
            </span>
          }
          className="mb-4 mt-4"
        >
          <div className="px-3 pb-4 pt-2 pl-4" style={{ lineHeight: 2 }}>
            <p className="mb-2">
              <Trans>To file this report, follow these steps:</Trans>
            </p>
            <ol className="mb-0 pl-3">
              <li className="mb-2">
                <Trans>Load data into each schedule. You have three options:</Trans>
                <ul className="mt-1 pl-3">
                  <li>
                    <strong>
                      <Trans>Manual entry:</Trans>
                    </strong>{' '}
                    <Trans>click on any schedule in the table and fill in the fields one by one.</Trans>
                  </li>
                  <li>
                    <strong>
                      <Trans>Bulk upload (Excel):</Trans>
                    </strong>{' '}
                    <Trans>upload a single Excel with data for several schedules. Available in the </Trans>
                    <a
                      onClick={() => {
                        const el = document.getElementById('bulk-section');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      style={{ color: isDarkMode ? '#7eb8d4' : '#4e7184', cursor: 'pointer' }}
                    >
                      <Trans>section at the bottom of this page</Trans>
                    </a>
                    .
                  </li>
                  <li>
                    <ApiFilingNote />
                  </li>
                </ul>
              </li>
              <li className="mb-2">
                <Trans>Verify the data for each schedule by entering them one by one. Adjust any values that need correction.</Trans>
              </li>
              <li className="mb-2">
                <Trans>
                  Mark each schedule as Complete using <em>Mark As Done</em>.
                </Trans>
              </li>
              <li>
                {hasInventorySchedules ? (
                  <Trans>
                    When all financial schedules are Done and every inventory schedule is Uploaded or marked Nothing to Report, use{' '}
                    <em>Submit for Review</em> to send the report to OCIF.
                  </Trans>
                ) : (
                  <Trans>
                    When all schedules are in Completed status, use <em>Submit for Review</em> to send the report to OCIF.
                  </Trans>
                )}
              </li>
            </ol>
          </div>
        </AccordionItem>

        {/* ── SCHEDULES TO COMPLETE ── */}
        <AccordionItem
          defaultOpen={false}
          title={
            <span className="form-subtitle">
              <Trans>Schedules to complete</Trans>
            </span>
          }
          className="mb-4 mt-4"
        >
          {/* ── STATUS FILTER CHIPS ── */}
          <div className="flex gap-2 items-center mt-2 mb-1">
            <div
              className="flex items-center gap-1 pl-3"
              style={{
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: isDarkMode ? '#878b9a' : '#6c757d',
                whiteSpace: 'nowrap',
              }}
            >
              <Trans>Filter by status</Trans>
              <button
                id="status-info-btn"
                type="button"
                style={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  border: `1.5px solid ${theme.gray400}`,
                  backgroundColor: `${theme.gray400}22`,
                  color: theme.gray400,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '8px',
                  flexShrink: 0,
                  lineHeight: 1,
                }}
              >
                <Icon icon="Info" />
              </button>
              <Tooltip
                target="status-info-btn"
                isOpen={statusInfoOpen}
                toggle={() => setStatusInfoOpen(v => !v)}
                placement="right"
                trigger="hover focus"
                autohide={false}
                fade={false}
                className="status-info-tooltip"
              >
                <ul className="mb-0 pl-3 py-1" style={{ lineHeight: 1.75 }}>
                  <li>
                    <strong>
                      <Trans>Not Uploaded</Trans>
                    </strong>
                    {' — '}
                    <Trans>the schedule has no uploaded data yet.</Trans>
                  </li>
                  <li>
                    <strong>
                      <Trans>Uploaded</Trans>
                    </strong>
                    {' — '}
                    <Trans>data loaded but not yet verified or marked as done.</Trans>
                  </li>
                  <li>
                    <strong>
                      <Trans>Partial Upload</Trans>
                    </strong>
                    {' — '}
                    <Trans>some required fields are missing. Cannot be submitted.</Trans>
                  </li>
                  <li>
                    <strong>
                      <Trans>Error</Trans>
                    </strong>
                    {' — '}
                    <Trans>data failed one or more validations. Correct before submitting.</Trans>
                  </li>
                  <li>
                    <strong>
                      <Trans>Done</Trans>
                    </strong>
                    {' — '}
                    <Trans>marked as done after verifying. Ready to submit.</Trans>
                  </li>
                  {hasInventorySchedules && (
                    <li>
                      <strong>
                        <Trans>Nothing to Report</Trans>
                      </strong>
                      {' — '}
                      <Trans>inventory schedule declared as having no data for this period.</Trans>
                    </li>
                  )}
                </ul>
              </Tooltip>
            </div>
            <div className="remarks-filter-row" style={{ marginBottom: 0 }}>
              {statusChips.map((status: UploadStatus) => (
                <FilterChip key={status} color={STATUS_COLOR[status]} active={activeStatuses.has(status)} onClick={() => toggleStatus(status)}>
                  <Icon icon={STATUS_ICON[status]} />
                  {status === 'Not Uploaded' ? (
                    <Trans>Not Uploaded</Trans>
                  ) : status === 'Uploaded' ? (
                    <Trans>Uploaded</Trans>
                  ) : status === 'Partial' ? (
                    <Trans>Partial Upload</Trans>
                  ) : status === 'Error' ? (
                    <Trans>Error</Trans>
                  ) : status === 'Done' ? (
                    <Trans>Done</Trans>
                  ) : (
                    <Trans>Nothing to Report</Trans>
                  )}
                </FilterChip>
              ))}
            </div>
          </div>
          {(scheduleTitles.length > 0 || !hasInventorySchedules) && (
            <ScheduleLinkList
              rows={visibleFinancialRows}
              onRowClick={row => openScheduleTab(row.key)}
              heading={hasInventorySchedules ? <Trans>Financial schedules</Trans> : undefined}
              emptyMessage={scheduleTitles.length > 0 ? noMatchesMessage : undefined}
            />
          )}
          {hasInventorySchedules && (
            <ScheduleLinkList
              rows={visibleInventoryRows}
              onRowClick={() => openScheduleTab(INVENTORY_SCHEDULES_TAB)}
              heading={<Trans>Inventory schedules</Trans>}
              emptyMessage={noMatchesMessage}
            />
          )}
        </AccordionItem>

        {/* ── BULK UPLOAD & API SECTION ── */}
        <div id="bulk-section" />
        <AccordionItem
          defaultOpen={false}
          title={
            <span className="form-subtitle">
              <Trans>Bulk upload (Excel)</Trans>
            </span>
          }
          className="mb-4 mt-4"
        >
          {/* ── Top row: dropzone (left) + file format details (right) ── */}
          <div className="flex gap-4 items-center  px-3 pb-3 pt-4" style={{ flexWrap: 'wrap' }}>
            {/* Right: file format details (inline, same style as Filing Options) */}
            <div className="pl-3" style={{ minWidth: 400, maxWidth: 850, lineHeight: 2 }}>
              <p className="mb-2">
                <Trans>
                  Excel workbook (<code>.xlsx</code>) — compatible with Microsoft Excel and Google Sheets; one tab per schedule.
                  <br />
                  Columns: <code>field_code | description | value</code>
                </Trans>
              </p>
              <ul className="mb-0 pl-3">
                <li>
                  <Trans>
                    <code>(FILL IN - integer)</code> — whole number (e.g. <code>42</code>); use <code>0</code> for zero-value fields
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <code>(FILL IN - number)</code> — decimal number (e.g. <code>1234.56</code>); use <code>0</code> for zero-value fields
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <code>(FILL IN - text)</code> — any text value
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <code>(FILL IN - a, b, or c)</code> — must exactly match one of the listed values (case-insensitive)
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <code>(FILL IN - date [YYYY-MM-DD])</code> — date in YYYY-MM-DD format <code>2025-01-15</code>.
                  </Trans>
                </li>
                <li>
                  <Trans>If a schedule already has data you will be asked to confirm before replacing it</Trans>
                </li>
              </ul>
            </div>

            {/* Left: download button + dropzone */}
            <div
              className="flex flex-col justify-center items-center"
              style={{ maxWidth: 375, width: '100%', flexShrink: 0, justifySelf: 'center', margin: 'auto' }}
            >
              <div className="mb-2 text-center">
                <Button
                  color="primary"
                  outline
                  size="sm"
                  className="small px-3 py-2"
                  disabled={financialTemplateLoading || scheduleTitles.length === 0}
                  onClick={handleFinancialDownload}
                >
                  {financialTemplateLoading ? (
                    <span className="flex items-center gap-4">
                      <Loader dots sm inline size={14} />
                      <Trans>Generating...</Trans>
                    </span>
                  ) : (
                    <>
                      <Icon icon="Download" className="mr-1" />
                      <Trans>Download Financial Report Template (.xlsx)</Trans>
                    </>
                  )}
                </Button>
              </div>
              <FinancialDropZone
                stage={financialStage}
                onFileSelected={handleFinancialFileSelected}
                onValidate={() => handleFinancialValidate()}
                onRetry={file => {
                  handleFinancialFileSelected(file);
                  handleFinancialValidate(file);
                }}
                onReplace={handleFinancialReplace}
                isMobile={isMobile}
              />
            </div>
          </div>

          {/* ── Status panels below the row ── */}
          {statusPanelCount > 0 && (
            <div className="pb-2">
              {statusPanelCount === 3 ? (
                <>
                  <div className="mx-5">
                    <FinancialNoticePanel result={financialResult!} />
                  </div>
                  <div className="flex gap-3 mx-5">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <IgnoredSchedulesNotice result={financialResult!} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <GlobalErrorPanel errors={financialResult!.globalErrors} />
                    </div>
                  </div>
                </>
              ) : statusPanelCount === 2 ? (
                <div className="flex gap-3 mx-5">
                  {showGlobalError && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <GlobalErrorPanel errors={financialResult!.globalErrors} />
                    </div>
                  )}
                  {showNotice && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <FinancialNoticePanel result={financialResult!} />
                    </div>
                  )}
                  {showIgnored && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <IgnoredSchedulesNotice result={financialResult!} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="mx-5">
                  {showGlobalError && <GlobalErrorPanel errors={financialResult!.globalErrors} />}
                  {showNotice && <FinancialNoticePanel result={financialResult!} />}
                  {showIgnored && <IgnoredSchedulesNotice result={financialResult!} />}
                </div>
              )}
            </div>
          )}
          <div className="px-3 pb-3">
            <ConflictsPanel conflicts={conflicts} formFieldsById={formFieldsById} navigateToField={navigateToField} readOnly={readonly || (UweFormContext?.formReadOnly ?? true)} className="mt-3" />
          </div>
        </AccordionItem>
      </div>
    </>
  );
}

export default OcifBulkUploadPanel;
